import { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { IndicatorsList, TwoColumnsList, Widget } from '@factorialco/f0-react/dist/experimental'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import { ProjectsWidgetDocument, ProjectsWidgetQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

const PROJECTS_LIMIT = 3

const prepareData = (data: ProjectsWidgetQuery) => {
  const records = data?.projectManagement.projects || []
  const totalMinutes = records.reduce((acc, project) => acc + (project.inputedMinutes ?? 0), 0)

  const sortedAndSlicedProjects = [...records]
    .sort((a, b) => (b.inputedMinutes ?? 0) - (a.inputedMinutes ?? 0))
    .slice(0, PROJECTS_LIMIT)

  return {
    title: i18n.t('insights.widgets.projects.title'),
    totalHours: minutesToText(totalMinutes),
    projects: sortedAndSlicedProjects.map(({ name, inputedMinutes }) => ({
      title: name ?? '',
      info: minutesToText(inputedMinutes ?? 0),
    })),
    showMoreCount: records.length - PROJECTS_LIMIT,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const buildProjectWidgetComponent = (contactType: FinanceValueObjectsContactTypeEnum) => {
  const ProjectsWidgetComponentWithContactType: FC<{
    data: PreparedData
  }> = ({ data: { title, totalHours, projects, showMoreCount } }) => {
    const { navigation } = useNavigation()
    const navigate = useNavigate()
    const { contactId } = useParams()

    const destinationUrl =
      contactType === 'client'
        ? navigation.finance.sales.clients.contactId.fullPath({ contactId: String(contactId) })
        : navigation.finance.spending.vendors.contactId.fullPath({ contactId: String(contactId) })

    const link = {
      title: i18n.t('insights.widgets.projects.title'),
      url: destinationUrl,
    }

    const handleNavigate = () => {
      navigate(destinationUrl)
    }

    return (
      <Widget
        header={{
          title,
          link,
        }}
        action={
          showMoreCount <= 0
            ? undefined
            : {
                label: i18n.tp('insights.widgets.projects.see_more', { count: showMoreCount }),
                onClick: handleNavigate,
              }
        }
      >
        <IndicatorsList
          items={[
            {
              label: i18n.t('insights.widgets.projects.total_hours'),
              content: totalHours,
            },
          ]}
        />
        {projects.length > 0 && <TwoColumnsList list={projects} />}
      </Widget>
    )
  }

  return ProjectsWidgetComponentWithContactType
}

export const buildProjectsWidget = (contactType: FinanceValueObjectsContactTypeEnum) =>
  buildWidget({
    id: 'projects',
    document: ProjectsWidgetDocument,
    prepareData,
    component: buildProjectWidgetComponent(contactType),
  })
